import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingWhatsApp from '../../components/feature/FloatingWhatsApp';
import CookieBanner from '../../components/feature/CookieBanner';
import BackToTop from '../../components/feature/BackToTop';
import { injectSchema, getBreadcrumbSchema, getWebPageSchema } from '../../utils/seoSchema';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    tipoPropiedad: '',
    superficie: '',
    municipio: '',
    ascensor: '',
    nivelDanos: '',
    urgencia: '',
    seguro: '',
    descripcion: '',
    privacidad: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contacto - Solicita Presupuesto Limpieza Post Incendio Barcelona | FIRETRAUMAS';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacta con FIRETRAUMAS para limpieza post incendio en Barcelona. Presupuesto sin compromiso. ☎ 690 22 09 44. Respuesta en menos de 2 horas.');
    }

    // Inject Schema.org JSON-LD
    const cleanupBreadcrumb = injectSchema(getBreadcrumbSchema([
      { name: 'Inicio', url: '/' },
      { name: 'Contacto', url: '/contacto' }
    ]));
    const cleanupWebPage = injectSchema(getWebPageSchema(
      'Contacto - Solicita Presupuesto Limpieza Post Incendio Barcelona',
      'Contacta con FIRETRAUMAS para limpieza post incendio en Barcelona. Presupuesto sin compromiso. Respuesta en menos de 2 horas.',
      '/contacto'
    ));

    return () => {
      cleanupBreadcrumb?.();
      cleanupWebPage?.();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacidad) {
      alert('Debes aceptar la Política de Privacidad para continuar.');
      return;
    }

    if (formData.descripcion.length > 500) {
      alert('La descripción no puede superar los 500 caracteres.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d54fvj3ontabvievu1sg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          telefono: '',
          email: '',
          tipoPropiedad: '',
          superficie: '',
          municipio: '',
          ascensor: '',
          nivelDanos: '',
          urgencia: '',
          seguro: '',
          descripcion: '',
          privacidad: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left - Info */}
              <div>
                <h1 className="font-serif font-bold text-5xl md:text-6xl text-primary mb-6">
                  Cuéntanos qué ha pasado
                </h1>
                <p className="text-textLight text-lg leading-relaxed mb-8">
                  Respuesta en menos de 2 horas en horario laboral. Presupuesto sin compromiso. Estamos aquí para ayudarte. Conoce más sobre <a href="/servicios" className="text-secondary font-semibold hover:text-primary transition-colors cursor-pointer">nuestros servicios de limpieza post incendio</a>.
                </p>

                {/* Contact Cards */}
                <div className="space-y-4 mb-8">
                  <a
                    href="tel:+34690220944"
                    className="flex items-center gap-4 bg-bg p-4 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="font-sans font-semibold text-primary">Teléfono</p>
                      <p className="text-secondary">+34 690 22 09 44</p>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/34690220944"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 bg-bg p-4 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-whatsapp-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="font-sans font-semibold text-primary">WhatsApp</p>
                      <p className="text-secondary">+34 690 22 09 44</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-4 bg-bg p-4 rounded-lg">
                    <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="font-sans font-semibold text-primary">Email</p>
                      <p className="text-textLight text-sm">info@firetraumas.com</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 bg-bg p-4 rounded-lg">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="font-sans font-semibold text-primary">Dirección</p>
                      <p className="text-textLight text-sm">Carrer de Septimània, 49<br />08006 Barcelona</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Map */}
              <div>
                <div className="bg-white rounded-lg overflow-hidden shadow-md h-[500px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2993.0234567890123!2d2.1540070000000003!3d41.403706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDI0JzEzLjMiTiAywrAwOScxNC40IkU!5e0!3m2!1ses!2ses!4v1234567890123!5m2!1ses!2ses"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Ubicación FIRETRAUMAS"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="form" className="py-20 bg-bg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg p-8 md:p-12 shadow-lg">
              <h2 className="font-serif font-bold text-3xl text-primary mb-6 text-center">
                Solicita tu presupuesto sin compromiso
              </h2>
              <p className="text-textLight text-center mb-8">
                Completa el formulario y nos pondremos en contacto contigo lo antes posible.
              </p>

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-semibold text-primary mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-sm font-semibold text-primary mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-primary mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="tipoPropiedad" className="block text-sm font-semibold text-primary mb-2">
                        Tipo de propiedad *
                      </label>
                      <select
                        id="tipoPropiedad"
                        name="tipoPropiedad"
                        value={formData.tipoPropiedad}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm cursor-pointer"
                      >
                        <option value="">Selecciona...</option>
                        <option value="Piso">Piso</option>
                        <option value="Casa">Casa</option>
                        <option value="Local comercial">Local comercial</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="superficie" className="block text-sm font-semibold text-primary mb-2">
                        Superficie aproximada (m²) *
                      </label>
                      <input
                        type="number"
                        id="superficie"
                        name="superficie"
                        value={formData.superficie}
                        onChange={handleChange}
                        required
                        min="1"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm"
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="municipio" className="block text-sm font-semibold text-primary mb-2">
                        Municipio *
                      </label>
                      <input
                        type="text"
                        id="municipio"
                        name="municipio"
                        value={formData.municipio}
                        onChange={handleChange}
                        required
                        placeholder="Ej: Barcelona, Badalona..."
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-primary mb-2">
                        ¿Hay ascensor? *
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="ascensor"
                            value="Sí"
                            checked={formData.ascensor === 'Sí'}
                            onChange={handleChange}
                            required
                            className="mr-2 cursor-pointer"
                          />
                          <span className="text-sm text-textPrimary">Sí</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="ascensor"
                            value="No"
                            checked={formData.ascensor === 'No'}
                            onChange={handleChange}
                            required
                            className="mr-2 cursor-pointer"
                          />
                          <span className="text-sm text-textPrimary">No</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="nivelDanos" className="block text-sm font-semibold text-primary mb-2">
                        Nivel de daños *
                      </label>
                      <select
                        id="nivelDanos"
                        name="nivelDanos"
                        value={formData.nivelDanos}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm cursor-pointer"
                      >
                        <option value="">Selecciona...</option>
                        <option value="Leve">Leve</option>
                        <option value="Moderado">Moderado</option>
                        <option value="Grave">Grave</option>
                        <option value="No estoy seguro/a">No estoy seguro/a</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="urgencia" className="block text-sm font-semibold text-primary mb-2">
                        Urgencia *
                      </label>
                      <select
                        id="urgencia"
                        name="urgencia"
                        value={formData.urgencia}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm cursor-pointer"
                      >
                        <option value="">Selecciona...</option>
                        <option value="Hoy">Hoy</option>
                        <option value="Esta semana">Esta semana</option>
                        <option value="Próxima semana">Próxima semana</option>
                        <option value="Sin prisa">Sin prisa</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-primary mb-2">
                        ¿Has contactado con el seguro? *
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="seguro"
                            value="Sí"
                            checked={formData.seguro === 'Sí'}
                            onChange={handleChange}
                            required
                            className="mr-2 cursor-pointer"
                          />
                          <span className="text-sm text-textPrimary">Sí</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="seguro"
                            value="No"
                            checked={formData.seguro === 'No'}
                            onChange={handleChange}
                            required
                            className="mr-2 cursor-pointer"
                          />
                          <span className="text-sm text-textPrimary">No</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="seguro"
                            value="Aún no"
                            checked={formData.seguro === 'Aún no'}
                            onChange={handleChange}
                            required
                            className="mr-2 cursor-pointer"
                          />
                          <span className="text-sm text-textPrimary">Aún no</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Full Width Fields */}
                <div className="mt-6">
                  <label htmlFor="descripcion" className="block text-sm font-semibold text-primary mb-2">
                    Describe qué ha pasado * (máx. 500 caracteres)
                  </label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Ej: Incendio en cocina, hollín en paredes y techo, olor fuerte..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-secondary transition-colors text-sm resize-none"
                  ></textarea>
                  <p className="text-xs text-textLight mt-1 text-right">
                    {formData.descripcion.length}/500 caracteres
                  </p>
                </div>

                {/* Privacy Checkbox */}
                <div className="mt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="privacidad"
                      checked={formData.privacidad}
                      onChange={handleChange}
                      required
                      className="mt-1 cursor-pointer"
                    />
                    <span className="text-sm text-textPrimary">
                      Acepto la{' '}
                      <a href="#privacidad" className="text-secondary underline hover:text-primary">
                        Política de Privacidad
                      </a>{' '}
                      *
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-opacity-90 transition-all inline-flex items-center justify-center whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar Solicitud
                        <i className="ri-send-plane-line ml-2"></i>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-textLight text-center mt-4">
                    Responderemos lo antes posible. Si es urgente, llama al{' '}
                    <a href="tel:+34690220944" className="text-secondary font-semibold cursor-pointer">
                      +34 690 22 09 44
                    </a>
                  </p>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="mt-6 bg-accent/10 border-l-4 border-accent rounded-lg p-4">
                    <p className="text-accent font-semibold">
                      ✓ Solicitud enviada correctamente. Nos pondremos en contacto contigo pronto.
                    </p>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                    <p className="text-red-700 font-semibold">
                      ✗ Error al enviar. Por favor, inténtalo de nuevo o llámanos directamente.
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        {/* Alternative Contact Methods */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif font-bold text-3xl text-primary mb-12 text-center">
              Otras formas de contactar
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <a
                href="tel:+34690220944"
                className="bg-bg rounded-lg p-8 text-center hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-line text-3xl text-white"></i>
                </div>
                <h3 className="font-sans font-bold text-xl text-primary mb-2">Llamar Ahora</h3>
                <p className="text-textLight text-sm">
                  Habla directamente con nosotros para una respuesta inmediata.
                </p>
              </a>

              <a
                href="https://wa.me/34690220944"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-bg rounded-lg p-8 text-center hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-whatsapp-line text-3xl text-white"></i>
                </div>
                <h3 className="font-sans font-bold text-xl text-primary mb-2">WhatsApp</h3>
                <p className="text-textLight text-sm">
                  Envía fotos y recibe respuesta rápida por WhatsApp.
                </p>
              </a>

              <a
                href="mailto:info@firetraumas.com"
                className="bg-bg rounded-lg p-8 text-center hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-3xl text-white"></i>
                </div>
                <h3 className="font-sans font-bold text-xl text-primary mb-2">Email</h3>
                <p className="text-textLight text-sm">
                  Escríbenos a info@firetraumas.com con tu consulta.
                </p>
              </a>
            </div>
          </div>
        </section>

        {/* Office Hours & Coverage */}
        <section className="py-20 bg-bg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-white rounded-lg p-8">
                <h3 className="font-serif font-bold text-2xl text-primary mb-6">
                  Horario de atención
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-textPrimary font-semibold">Lunes a Viernes:</span>
                    <span className="text-textLight">8:00 – 18:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-textPrimary font-semibold">Sábados:</span>
                    <span className="text-textLight">9:00 – 14:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-textPrimary font-semibold">Domingos y festivos:</span>
                    <span className="text-textLight">Consultar</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8">
                <h3 className="font-serif font-bold text-2xl text-primary mb-6">
                  Zona de cobertura
                </h3>
                <p className="text-textLight mb-4">
                  Barcelona ciudad y Área Metropolitana de Barcelona (AMB)
                </p>
                <a
                  href="/#cobertura"
                  className="text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer"
                >
                  Ver mapa de cobertura completo →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-textLight mb-4">¿Prefieres leer más primero?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/servicios" className="text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer">
                Ver servicios especializados →
              </a>
              <a href="/faq" className="text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer">
                Consultar preguntas frecuentes →
              </a>
              <a href="/empresa" className="text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer">
                Conocer nuestra empresa →
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <CookieBanner />
      <BackToTop />
    </div>
  );
}